import type { Layout, Pose, SpherePoint } from '@/components/riffle/types'

import { N } from '@/components/riffle/data'
import {
    DEG,
    clamp,
    easeInOut,
    lerp,
    lerpPose,
    rand,
    smoother
} from '@/components/riffle/math'

const PERSP = 1400
const NAV_SAFE = 34
const BROWSE_START = 0.8
const STAGGER = 0.22

// ─── §9.0 Arch ──────────────────────────────────────────────

export const fArch = (i: number, L: Layout): Pose => {
    const { W, H, card } = L
    const u = i / (N - 1)
    const ang = lerp(228, -48, u) * DEG
    const R = Math.min(W, H) * 0.33
    const narrow = W < 900
    const sMax = narrow ? 0.8 : 1.0
    const cardHalf = card * sMax * 0.56
    const Rx = Math.min(R * 1.18, (W / 2 - cardHalf - 16) / 1.06)

    return {
        x: Math.cos(ang) * Rx,
        y: -Math.sin(ang) * R,
        z: lerp(120, -150, u),
        rx: 0,
        ry: lerp(8, -8, u),
        rz: lerp(-16, 16, u),
        s: lerp(sMax, narrow ? 0.42 : 0.46, u),
        o: 1
    }
}

// ─── §9.1 Collapse ──────────────────────────────────────────

export const fCollapse = (i: number, L: Layout): Pose => {
    const { W, H } = L
    return {
        x: W * 0.04 + (rand(i) - 0.5) * 10,
        y: H * 0.03 + (rand(i + 9) - 0.5) * 10,
        z: -i * 1.4,
        rx: 0,
        ry: 0,
        rz: (rand(i + 3) - 0.5) * 6,
        s: i === 0 ? 0.78 : 0.72,
        o: 1
    }
}

// ─── §9.2 Cascade ───────────────────────────────────────────

export const fCascade = (i: number, L: Layout): Pose => {
    const { W, H } = L
    const u = i / (N - 1)
    return {
        x: W * 0.2 - i * W * 0.04,
        y: H * 0.18 - i * H * 0.044,
        z: -i * 42,
        rx: 8,
        ry: -16,
        rz: -4,
        s: lerp(1.0, 0.74, u),
        o: i < 12 ? 1 : lerp(1, 0.4, (i - 12) / Math.max(1, N - 1 - 12))
    }
}

// ─── §9.3 Pinwheel ──────────────────────────────────────────

export const fPinwheel = (i: number, L: Layout): Pose => {
    const { W, H, mobile } = L
    const R = Math.min(W, H) * (mobile ? 0.42 : 0.37)
    const phiDeg = i * (360 / N)
    const phi = phiDeg * DEG
    return {
        x: Math.cos(phi) * R,
        y: Math.sin(phi) * R,
        z: 0,
        rx: 0,
        ry: 0,
        rz: phiDeg + 90,
        s: mobile ? 0.34 : 0.5,
        o: 1
    }
}

// ─── §9.4 Sphere ────────────────────────────────────────────
// (a) narrow / portrait → clean 4-column grid; (b) desktop → de-clumped globe.

const fSphereGrid = (i: number, L: Layout): Pose => {
    const { W, H, card } = L
    const cols = 4
    const rows = Math.ceil(N / cols) // 6
    const row = Math.floor(i / cols)
    const col = i % cols
    const cardsInRow = Math.min(cols, N - row * cols)
    const sideMargin = W * 0.06
    const topMargin = NAV_SAFE + H * 0.05
    const botMargin = H * 0.05
    const pitch = Math.min(
        (W - 2 * sideMargin) / cols,
        (H - topMargin - botMargin) / rows
    )
    const S = (pitch * 0.82) / card
    const cyOff = (topMargin - botMargin) / 2

    return {
        x: (col - (cardsInRow - 1) / 2) * pitch,
        y: cyOff + (row - (rows - 1) / 2) * pitch,
        z: 0,
        rx: 0,
        ry: 0,
        rz: (i % 2 === 0 ? 1 : -1) * 2.2,
        s: S,
        o: 1
    }
}

// Build + cache the de-clumped volumetric globe for a viewport (§9.4b).
export const buildGlobe = (L: Layout): SpherePoint[] => {
    const { W, H, card } = L
    const R = Math.min(W, H) * 0.38
    const S = 0.8

    // 1. Fibonacci sphere, projected to screen space.
    const pts = [] as { sx: number; sy2: number; sy: number; z: number; size: number }[]
    for (let i = 0; i < N; i++) {
        const k = i + 0.5
        const phi = Math.acos(1 - (2 * k) / N)
        const theta = Math.PI * (1 + Math.sqrt(5)) * k
        const sy = Math.sin(phi) * Math.sin(theta)
        const z = sy * R
        const persp = PERSP / (PERSP - z)
        const sx = Math.sin(phi) * Math.cos(theta) * R * 1.6 * persp
        const sy2 = Math.cos(phi) * R * persp
        const size = card * S * persp
        pts.push({ sx, sy2, sy, z, size })
    }

    // 2. AABB Jacobi separation — keeps cards on-screen + apart.
    const half = (card * S * 1.12) / 2
    const maxX = W / 2 - half - 16
    const maxY = H / 2 - NAV_SAFE - half - 16

    const settle = (iters: number): void => {
        for (let it = 0; it < iters; it++) {
            const push = pts.map(() => ({ x: 0, y: 0 }))
            let moved = false
            for (let a = 0; a < N; a++) {
                for (let b = a + 1; b < N; b++) {
                    const dx = pts[b].sx - pts[a].sx
                    const dy = pts[b].sy2 - pts[a].sy2
                    const req = ((pts[a].size + pts[b].size) / 2) * 1.06
                    const adx = Math.abs(dx)
                    const ady = Math.abs(dy)
                    if (adx < req && ady < req) {
                        const penX = req - adx
                        const penY = req - ady
                        if (penX < penY) {
                            const s = ((dx < 0 ? -1 : 1) * penX) / 2
                            push[a].x -= s
                            push[b].x += s
                        } else {
                            const s = ((dy < 0 ? -1 : 1) * penY) / 2
                            push[a].y -= s
                            push[b].y += s
                        }
                        moved = true
                    }
                }
            }
            for (let i = 0; i < N; i++) {
                pts[i].sx = clamp(pts[i].sx + push[i].x * 0.5, -maxX, maxX)
                pts[i].sy2 = clamp(pts[i].sy2 + push[i].y * 0.5, -maxY, maxY)
            }
            if (!moved) break
        }
    }

    settle(300)

    // 3. Hand nudge card 16 into the empty pocket, then re-settle.
    pts[16].sx = clamp(pts[16].sx - 0.025 * W, -maxX, maxX)
    pts[16].sy2 = clamp(pts[16].sy2 + 0.079 * H, -maxY, maxY)
    settle(80)

    // 4. Convert back to deck space.
    return pts.map((p) => {
        const persp = PERSP / (PERSP - p.z)
        return { x: p.sx / persp, y: p.sy2 / persp, z: p.z, sy: p.sy }
    })
}

export const fSphere = (i: number, L: Layout, sphere: SpherePoint[]): Pose => {
    if (L.narrow) return fSphereGrid(i, L)
    const pt = sphere[i]
    return {
        x: pt.x,
        y: pt.y,
        z: pt.z,
        rx: 0,
        ry: 0,
        rz: 0,
        s: 0.8,
        o: pt.sy >= 0 ? 1 : lerp(1, 0.66, -pt.sy)
    }
}

// ─── §9.5 Carousel (infinite coverflow) ─────────────────────

export const fCarousel = (i: number, L: Layout, focus: number): Pose => {
    const { W, card, mobile } = L
    let slot = (((i - focus) % N) + N) % N
    if (slot > N / 2) slot -= N
    const a = Math.abs(slot)
    const spacing = mobile ? W * 0.62 : card * 2.28

    return {
        x: slot * spacing,
        y: 0,
        z: -a * 140,
        rx: 0,
        ry: clamp(-slot * 26, -54, 54),
        rz: 0,
        s: mobile ? Math.max(0.5, 2.3 - a * 0.85) : Math.max(0.6, 2.0 - a * 0.25),
        o: a > 3 ? 0 : a <= 1 ? 1 : clamp(1 - (a - 1) * 0.5, 0, 1)
    }
}

// ─── §10 Formation engine ───────────────────────────────────

const formPose = (
    idx: number,
    i: number,
    L: Layout,
    sphere: SpherePoint[]
): Pose => {
    switch (idx) {
        case 0:
            return fArch(i, L)
        case 1:
            return fCollapse(i, L)
        case 2:
            return fCascade(i, L)
        case 3:
            return fPinwheel(i, L)
        case 4:
            return fSphere(i, L, sphere)
        default:
            return fCarousel(i, L, 0)
    }
}

// resolvePose — blends consecutive formations with the hold-morph-hold remap
// and a per-card stagger wave; hands off to the live carousel past BROWSE_START.
export const resolvePose = (
    i: number,
    P: number,
    L: Layout,
    focus: number,
    sphere: SpherePoint[]
): Pose => {
    if (P >= BROWSE_START) return fCarousel(i, L, focus)

    const SEG_W = BROWSE_START / 5 // 0.16
    const seg = clamp(Math.floor(P / SEG_W), 0, 4)
    const fLocal = clamp((P - seg * SEG_W) / SEG_W, 0, 1)
    const morph = smoother((fLocal - 0.12) / 0.76)
    const ph = (i / (N - 1)) * STAGGER
    const t = easeInOut(clamp(morph * (1 + STAGGER) - ph, 0, 1))

    return lerpPose(formPose(seg, i, L, sphere), formPose(seg + 1, i, L, sphere), t)
}
